import { useMemo } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { measurementType } from '@gk/contracts'
import { areaLabel, type AreaRow, type AreaVersionRow } from '../../data/geo.ts'
import { initials } from '../../data/liveMap.ts'
import { useNotebook } from '../../data/measure.ts'
import { jobsOn, latestReadings, withDescendants } from '../../data/mapJobs.ts'
import type { PlanJob } from '../../data/plan.ts'
import { useNames } from '../../data/planUi.ts'
import { formatArea } from '../../lib/geo.ts'
import { fmtNum } from '../../lib/format.ts'
import { formatLocal } from '../../lib/time.ts'
import { Pill } from '../../ui/components.tsx'
import { useWriteAccess } from '../../ui/hooks.ts'
import { VerdictPill, typeLabel, unitSymbol } from '../measures/shared.tsx'
import { accuracyText } from './InfraPanel.tsx'

/** At most this many of the day's jobs in the popup (a loop holds the day's whole board); the rest are counted. */
export const AREA_JOBS_SHOWN = 12

/** M16-E: the map's panel on an area. What it is and how well its outline is known (M6), then what is on it TODAY — the day's jobs on it and on the areas under
 *  it (a hole's green and tee), each with its state, its crew's initials and "Ouvrir" — and the latest reading of each type with its verdict against the club's
 *  target, all from the device's copy; "Signaler une observation" opens the observation form with the area chosen (M16-N). */
export function AreaPanel({ area, version, path, areas, byArea, lang, tz, canEdit }: {
  area: AreaRow; version: AreaVersionRow | undefined; path: AreaRow[]; areas: ReadonlyArray<Pick<AreaRow, 'id' | 'parent_id'>>; byArea: ReadonlyMap<string, readonly PlanJob[]>; lang: string; tz: string; canEdit: boolean
}) {
  const { t } = useTranslation()
  const observe = useWriteAccess('measurement.record')
  const jobs = useMemo(() => jobsOn(byArea, withDescendants(area.id, areas)), [byArea, area.id, areas])
  return (
    <div className="flex flex-col gap-2" data-testid="area-panel" data-area={area.id}>
      <h2 className="text-lg font-bold" data-testid="info-title">{areaLabel(area, lang)}</h2>
      <p className="meta">{[...path.map((p) => areaLabel(p, lang)), t(`areaType.${area.area_type_code}`)].join(' › ')}</p>
      {version ? (
        <p className="text-sm" data-testid="info-meta">
          {formatArea(Number(version.area_m2), lang)} · {t('map.version', { n: version.version_no })} · {formatLocal(version.captured_on, tz, 'dd/MM/yyyy')} · {accuracyText(t, version.source, version.accuracy_m, lang)}
        </p>
      ) : <Pill tone="amber">{t('course.notDrawn')}</Pill>}
      <div className="flex flex-wrap gap-2">
        <Link to="/course" search={{ area: area.id }} className="btn !px-4" data-testid="info-course">{t('map.inCourse')}</Link>
        {canEdit && <Link to="/course/$areaId/shape" params={{ areaId: area.id }} className="btn btn-primary !px-4" data-testid="info-redraw">{version ? t('course.redraw') : t('course.draw')}</Link>}
        {observe.permitted && <Link to="/observe" search={{ area: area.id, from: 'map' }} className="btn !px-4" data-testid="info-observe">{t('map.area.observe')}</Link>}
      </div>
      <DayJobs jobs={jobs} />
      <Readings area={area} lang={lang} />
    </div>
  )
}

/** The day's jobs on the area (and on the areas under it): the title, the job's own state as the board says it, the crew's initials, "Ouvrir". */
function DayJobs({ jobs }: { jobs: readonly PlanJob[] }) {
  const { t } = useTranslation()
  const names = useNames()
  const shown = jobs.slice(0, AREA_JOBS_SHOWN)
  return (
    <section className="flex flex-col gap-2 border-t border-slate-200 pt-2 dark:border-night-line" data-testid="map-area-jobs" data-count={jobs.length}>
      <h3 className="text-base font-bold">{t('map.area.jobsTitle')}</h3>
      {jobs.length === 0 && <p className="meta" data-testid="map-area-jobs-none">{t('map.area.jobsNone')}</p>}
      {shown.length > 0 && (
        <ul className="flex flex-col gap-2">
          {shown.map((j) => (
            <li key={j.id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-2 pl-3 dark:border-night-line" data-testid="area-job" data-job={j.id} data-state={j.state}>
              <span className="flex min-w-0 flex-col gap-1">
                {/* release b review (finding 11): the title wraps (truncated at 375 px, two jobs of one area could not be told apart) */}
                <span className="min-w-0 break-words font-semibold" data-testid="area-job-title">{j.title_text}</span>
                <span className="flex flex-wrap items-center gap-1">
                  <Pill tone={j.state === 'done' ? 'green' : j.state === 'planned' ? 'gray' : 'amber'}>{t(`plan.state.${j.state}`)}</Pill>
                  {j.crew.length === 0 && <span className="meta" data-testid="area-job-nobody">{t('map.area.crewNone')}</span>}
                  {j.crew.map((c) => {
                    const name = names?.get(c.person_id) ?? '?'
                    return <span key={c.id} className="inline-flex size-8 items-center justify-center rounded-full bg-slate-200 text-[13px] font-bold text-slate-900 dark:bg-night-line dark:text-white" title={name} aria-label={name} data-testid="area-job-initials">{initials(name)}</span>
                  })}
                </span>
              </span>
              <Link to="/plan/job/$jobId" params={{ jobId: j.id }} className="btn shrink-0 !px-4" data-testid="area-job-open">{t('map.area.jobOpen')}</Link>
            </li>
          ))}
        </ul>
      )}
      {jobs.length > shown.length && <p className="meta" data-testid="map-area-jobs-more">{t('map.area.jobsMore', { count: jobs.length - shown.length })}</p>}
    </section>
  )
}

/** The latest day's mean of each type measured on the area, against the club's target of that month (no target, no verdict: nothing is called a standard). */
function Readings({ area, lang }: { area: AreaRow; lang: string }) {
  const { t } = useTranslation()
  const nb = useNotebook()
  const rows = useMemo(() => latestReadings(nb.readings, nb.targets, area.id, area.area_type_code), [nb.readings, nb.targets, area.id, area.area_type_code])
  if (rows.length === 0) return null
  return (
    <section className="flex flex-col gap-2 border-t border-slate-200 pt-2 dark:border-night-line" data-testid="map-area-readings" data-count={rows.length}>
      <h3 className="text-base font-bold">{t('map.area.readingsTitle')}</h3>
      <ul className="flex flex-col gap-1">
        {rows.map((r) => {
          const def = measurementType(r.type)
          return (
            <li key={r.type} className="flex flex-wrap items-baseline justify-between gap-2" data-testid="area-reading" data-type={r.type} data-verdict={r.verdict ?? ''}>
              <span className="min-w-0">
                <span className="font-semibold">{typeLabel(def, lang) || r.type}</span>
                <span className="meta"> · {r.date.split('-').reverse().join('/')}</span>
              </span>
              <span className="flex flex-wrap items-center gap-2">
                <span className="font-bold" data-testid="area-reading-value">{fmtNum(r.mean, lang, def?.decimals ?? 2)} {def ? unitSymbol(def.unit, lang) : ''}</span>
                <VerdictPill verdict={r.verdict} target={r.target} unit={def?.unit ?? ''} />
              </span>
            </li>
          )
        })}
      </ul>
      <p className="meta">{t('map.area.readingsHelp')}</p>
    </section>
  )
}
